import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

const PERSON_SIZE = 60;

export class AddPersonTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-person',
    name: i18n.t('genogram:tools.addPerson', { defaultValue: 'Add Person' }) as string,
    icon: '👤',
    category: 'create',
    description: 'Add a new person to the genogram',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: false,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '1', description: 'Add a person (when no selection)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addPerson', 'Add Person');
  }

  canExecute(context: IToolContext): boolean {
    // Can only execute when nothing is selected
    return context.selection.size === 0;
  }

  execute(params?: { sex?: 'male' | 'female' | 'unknown'; x?: number; y?: number }): ICommand | null {
    const state = useDiagramStore.getState();

    // Use provided position or default to center of viewport
    const x = params?.x ?? (state.stage.offsetX + 400);
    const y = params?.y ?? (state.stage.offsetY + 300);

    const sex = params?.sex || 'unknown';
    const newNodeId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: Node = {
      id: newNodeId,
      type: 'person',
      name: i18n.t('genogram:person.label', { defaultValue: 'Person' }) as string,
      sex,
      x,
      y,
      w: PERSON_SIZE,
      h: PERSON_SIZE,
      category: 'genogram',
    } as Node;

    return new CreateNodeCommand(newNode);
  }
}
